import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  dataAccessAdminService,
  DataAccessRequestRow,
} from '../services/dataAccessAdminService';

// FEAT-078 — Admin data access request queue (MVP-18.46).

export default function DataAccessRequestQueuePage() {
  const [requests, setRequests] = useState<DataAccessRequestRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [denyTarget, setDenyTarget] = useState<DataAccessRequestRow | null>(null);
  const [denialReason, setDenialReason] = useState('');
  const [fulfillNotes, setFulfillNotes] = useState('');

  const loadQueue = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await dataAccessAdminService.listQueue();
      setRequests(rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load queue');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadQueue();
  }, [loadQueue]);

  const handleFulfill = async (request: DataAccessRequestRow) => {
    setSubmitting(true);
    setError(null);
    try {
      await dataAccessAdminService.fulfill(request.id, fulfillNotes.trim() || undefined);
      setFulfillNotes('');
      await loadQueue();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fulfill request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeny = async () => {
    if (!denyTarget || !denialReason.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await dataAccessAdminService.deny(denyTarget.id, denialReason.trim());
      setDenyTarget(null);
      setDenialReason('');
      await loadQueue();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to deny request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }} data-testid="data-access-queue-page">
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Data Access Requests
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review MFA-verified export and deletion requests (MVP-18.46).
          </Typography>
        </Box>
        <Button startIcon={<RefreshIcon />} onClick={() => void loadQueue()} disabled={loading}>
          Refresh
        </Button>
      </Stack>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>MFA Verified</TableCell>
                <TableCell>Submitted</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.map((request) => (
                <TableRow key={request.id} data-testid={`data-access-row-${request.id}`}>
                  <TableCell>
                    {request.user
                      ? `${request.user.firstName ?? ''} ${request.user.lastName ?? ''}`.trim() ||
                        request.user.email
                      : request.userId}
                  </TableCell>
                  <TableCell>{request.requestType}</TableCell>
                  <TableCell>
                    <Chip
                      label={request.mfaVerifiedAt ? 'Verified' : 'Missing'}
                      color={request.mfaVerifiedAt ? 'success' : 'warning'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{new Date(request.createdAt).toLocaleString()}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button
                        size="small"
                        color="success"
                        onClick={() => void handleFulfill(request)}
                        disabled={submitting}
                        data-testid={`fulfill-request-${request.id}`}
                      >
                        Fulfill
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => setDenyTarget(request)}
                        disabled={submitting}
                        data-testid={`deny-request-${request.id}`}
                      >
                        Deny
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {requests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No queued requests.
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={Boolean(denyTarget)} onClose={() => setDenyTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Deny Request</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Denial reason"
            value={denialReason}
            onChange={(e) => setDenialReason(e.target.value)}
            margin="normal"
            multiline
            minRows={2}
            inputProps={{ 'data-testid': 'denial-reason-input' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDenyTarget(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => void handleDeny()}
            disabled={submitting || !denialReason.trim()}
            data-testid="confirm-deny-btn"
          >
            Deny
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
